import { graphql, useStaticQuery } from 'gatsby'
import React, { memo, useCallback, useRef, useState } from 'react'

import reviewVideo from '../images/reviews/Jabrille_Lotus_Trading.mp4'

const MuteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
  </svg>
)

const UnmuteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
)

/**
 * The news cards paint their image as a CSS background, so there is no <img>
 * element to hang a srcSet on and no lazy-loading to opt into. The next best
 * thing is to stop shipping the original: the fluid queries below cap these at
 * 720px (the card is 343px wide at 390px, 456px at 1440px) and emit WebP.
 * Before this, eme_story.png alone was a 2.3MB 3500x2000 PNG painted into a
 * 343x148 box.
 */
const bg = node => {
  const f = node && node.childImageSharp && node.childImageSharp.fluid
  return f ? f.srcWebp || f.src : undefined
}

const News = ({ isAbout }) => {
  const queryData = useStaticQuery(graphql`
    query NewsQuery {
      Bigjerryfencing: file(relativePath: { eq: "news/Bigjerryfencing.jpg" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      yazoPOS1: file(relativePath: { eq: "news/yazoPOS1.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      nurtureful: file(relativePath: { eq: "caseStudies/nurturefulth.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      muscatMMG: file(relativePath: { eq: "caseStudies/mediamuscatgroup.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      JabrilleLotus: file(relativePath: { eq: "reviews/Jabrille_Lotus_Trading.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      orangeline: file(relativePath: { eq: "caseStudies/orangelinetrain.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
      eme: file(relativePath: { eq: "caseStudies/eme_story.png" }) {
        childImageSharp { fluid(maxWidth: 720, quality: 72) { src srcWebp } }
      }
    }
  `)

  const items = [
    // {
    //   type: 'card',
    //   image: bg(queryData.JabrilleLotus),
    //   badge: 'Client Story',
    //   title: 'Jabrille Lotus Trading — See how they transformed their business with Visiomate',
    //   // cta: 'Read now',
    //   // href: '/caseStudies/projects/muscatMediaGroup',
    // },
    {
      type: 'video',
      badge: 'Client Story',
      title: 'Jabrille Lotus Trading — See how they transformed their business with Visiomate',
    },
    {
      type: 'card',
      image: bg(queryData.muscatMMG),
      badge: 'Customer Story',
      title: 'Automated HR Management System for Muscat Media Group — Oman\'s leading media conglomerate',
      cta: 'Read now',
      href: '/caseStudies/projects/muscatMediaGroup',
    },
    {
      type: 'card',
      image: bg(queryData.eme),
      bgStyle: { background: 'linear-gradient(135deg, #0a3d6b 0%, #1a6b8a 50%, #0d4a3e 100%)' },
      badge: 'Customer Story',
      title: 'Procurement Security & Multi-Currency Finance for EME Aquatic Systems — Abu Dhabi, UAE',
      cta: 'Read now',
      href: '/caseStudies/projects/emeAquaticSystems',
    },
    {
      type: 'card',
      image: bg(queryData.orangeline),
      badge: 'Customer Story',
      title: 'Construction Management Solution for Orangeline Pakistan — from planning to handover',
      cta: 'Read now',
      href: '/caseStudies/projects/orangelinePakistan',
    },
    {
      type: 'card',
      image: bg(queryData.Bigjerryfencing),
      badge: 'Customer Story',
      title: "Complete Digital Transformation for Big Jerry's Fencing from lead intake to delivery",
      cta: 'Read now',
      href: '/caseStudies/projects/bigJerrysFencing',
    },
    {
      type: 'card',
      image: bg(queryData.nurtureful),
      badge: 'Customer Story',
      title: 'End-to-End Digital Transformation for Nurtureful Psychology',
      cta: 'Read now',
      href: '/caseStudies/projects/nurturefulPsychology',
    },
  ]

  const PER_PAGE = 3
  const pageCount = Math.ceil(items.length / PER_PAGE)

  const [page, setPage] = useState(0)
  const [muted, setMuted] = useState(true)
  const videoRef = useRef(null)

  const prev = useCallback(() => setPage(p => (p - 1 + pageCount) % pageCount), [pageCount])
  const next = useCallback(() => setPage(p => (p + 1) % pageCount), [pageCount])

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setMuted(m => !m)
    }
  }

  const visible = items.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  return (
    <section className="ns-section">
      {/* Header */}
      <div className="ns-header">
        <span className="ns-eyebrow">
          {isAbout ? 'Want to Discover More?' : 'Customer Stories'}
        </span>
        <h2 className="ns-heading">
          {isAbout
            ? "See what we've built for our clients"
            : 'Real projects. Real outcomes. Real clients.'}
        </h2>
      </div>

      {/* Unified carousel grid */}
      <div className="ns-grid">
        {visible.map((item, i) =>
          item.type === 'video' ? (
            <div key={i} className="ns-card ns-card--video">
              <video
                ref={videoRef}
                className="ns-card__video"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={reviewVideo} type="video/mp4" />
              </video>
              <span className="ns-card__badge">{item.badge}</span>
              <div className="ns-card__footer">
                <p className="ns-card__title">{item.title}</p>
                <button
                  className={`ns-mute-btn${muted ? ' ns-mute-btn--muted' : ''}`}
                  onClick={toggleMute}
                  aria-label={muted ? 'Unmute video' : 'Mute video'}
                >
                  {muted ? <MuteIcon /> : <UnmuteIcon />}
                  <span>{muted ? 'Unmute' : 'Mute'}</span>
                </button>
              </div>
            </div>
          ) : (
            <a
              key={i}
              href={item.href}
              className="ns-card"
              style={item.image
                ? { backgroundImage: `url(${item.image})` }
                : item.bgStyle || {}
              }
              target={item.href?.startsWith('http') ? '_blank' : undefined}
              rel={item.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              <span className="ns-card__badge">{item.badge}</span>
              <div className="ns-card__footer">
                <p className="ns-card__title">{item.title}</p>
                {item.cta && <span className="ns-card__cta">{item.cta}</span>}
              </div>
            </a>
          )
        )}
      </div>

      {/* Navigation */}
      <div className="ns-nav">
        <button className="ns-nav__arrow" onClick={prev} aria-label="Previous">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="ns-nav__dots">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              className={`ns-nav__dot${i === page ? ' ns-nav__dot--active' : ''}`}
              onClick={() => setPage(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
        <button className="ns-nav__arrow" onClick={next} aria-label="Next">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  )
}

export default memo(News)
